/**
 * Pure derivation logic for the "Benchmarks" panel and the fleet card's
 * one-liner (lane BEN — live benchmarking per fly), split from
 * `BenchmarksPanel.ts` so every rule is unit-testable without the DOM
 * (mirrors `sizingView.ts`). Reads only `index.json.refs.benchmarks`
 * (`organism.benchmark.metrics.build_benchmark_report`); nothing here
 * computes a statistic the publisher did not — it only labels, orders,
 * and reshapes what was published, and degrades every missing value to
 * "–" (never a guessed 0).
 */

import type {
  BenchmarkRegimeDay,
  BenchmarkSeriesName,
  BenchmarkVerdict,
  BenchmarksDocument,
} from "../types/incremental";
import type { RoleSnapshot } from "../data/snapshotIndex";

/** Fixed legend/colour order — colour follows the entity, never its rank. */
export const SERIES_ORDER: readonly BenchmarkSeriesName[] = [
  "live",
  "buy_and_hold",
  "core_only",
  "buy_and_hold_monthly",
  "cash",
];

export const SERIES_LABELS: Readonly<Record<BenchmarkSeriesName, string>> = {
  live: "Live",
  buy_and_hold: "Buy & hold",
  core_only: "Core only (60%)",
  buy_and_hold_monthly: "B&H monthly",
  cash: "Cash",
};

export const SERIES_TOOLTIPS: Readonly<Record<BenchmarkSeriesName, string>> = {
  live: "The organism's own W(t): broker equity minus accrued liabilities minus the cost of exiting what it holds.",
  buy_and_hold:
    "Equal weight of the universe at the role's gross cap, bought at the first sane ask after genesis and never rebalanced — the gate's benchmark.",
  core_only:
    "What the live core_tilt policy's 60% equal-weight core alone would hold (same build-in ramp, bands, governor and turnover budget, no tilt).",
  buy_and_hold_monthly:
    "The same equal-weight book, rebalanced back to equal weight at the start of each UTC month.",
  cash: "The opening capital, paying the same operating tariff.",
};

/** Validated categorical slots for the dark surface (dataviz validator: all
 * checks pass for the first four, adjacent pairlist); cash is a neutral,
 * dashed reference line rather than a fifth categorical hue. */
export const SERIES_COLORS: Readonly<Record<BenchmarkSeriesName, string>> = {
  live: "#3987e5",
  buy_and_hold: "#d95926",
  core_only: "#199e70",
  buy_and_hold_monthly: "#c98500",
  cash: "#5c667a",
};

/** Dashed (secondary encoding beside the colour) for the two reference lines. */
export const SERIES_DASHED: ReadonlySet<BenchmarkSeriesName> = new Set([
  "buy_and_hold_monthly",
  "cash",
]);

export type BenchmarksPanelState =
  | { readonly kind: "empty" }
  | { readonly kind: "pending"; readonly message: string }
  | { readonly kind: "ready"; readonly doc: BenchmarksDocument };

export function buildBenchmarksPanelState(doc: BenchmarksDocument | null): BenchmarksPanelState {
  if (!doc) return { kind: "empty" };
  if (doc.status === "no_genesis") {
    return {
      kind: "pending",
      message:
        "No episode genesis recorded yet (no env_account_opened / portfolio.snapshot in this catalog).",
    };
  }
  if (doc.status === "no_samples" || !doc.stats || !doc.series) {
    return {
      kind: "pending",
      message: "Genesis recorded; waiting for the first portfolio.snapshot cycle.",
    };
  }
  return { kind: "ready", doc };
}

export interface ChartPoint {
  readonly time: number;
  readonly value: number;
}

/** One series' chart points: whole seconds, strictly increasing (a later
 * point at the same second replaces the earlier one — the chart library
 * rejects duplicates), nulls dropped. */
export function chartPoints(doc: BenchmarksDocument, name: BenchmarkSeriesName): ChartPoint[] {
  const series = doc.series;
  if (!series) return [];
  const values = series[name] ?? [];
  const out: ChartPoint[] = [];
  for (let i = 0; i < series.t.length; i += 1) {
    const value = values[i];
    const ms = Date.parse(series.t[i] ?? "");
    if (value === null || value === undefined || !Number.isFinite(value) || !Number.isFinite(ms)) {
      continue;
    }
    const time = Math.floor(ms / 1000);
    const last = out[out.length - 1];
    if (last && time <= last.time) {
      if (time === last.time) out[out.length - 1] = { time, value };
      continue;
    }
    out.push({ time, value });
  }
  return out;
}

export interface VerdictText {
  readonly text: string;
  /** The pill class (panel). */
  readonly className: string;
  /** A text-only tone class (fleet card), from the shared value tones. */
  readonly toneClass: string;
}

export function verdictText(
  status: BenchmarkVerdict | undefined,
  counted?: number,
  minimum?: number,
): VerdictText {
  switch (status) {
    case "pass":
      return { text: "Pass", className: "obs-bench-verdict-pass", toneClass: "obs-value-up" };
    case "fail":
      return { text: "Fail", className: "obs-bench-verdict-fail", toneClass: "obs-value-down" };
    case "insufficient_data": {
      const progress =
        counted !== undefined && minimum !== undefined ? ` · ${counted}/${minimum} days` : "";
      return {
        text: `Insufficient data${progress}`,
        className: "obs-bench-verdict-pending",
        toneClass: "obs-value-flat",
      };
    }
    default:
      return { text: "–", className: "obs-bench-verdict-pending", toneClass: "obs-value-flat" };
  }
}

const CHECK_LABELS: Readonly<Record<string, string>> = {
  beats_cash: "Beats cash",
  max_drawdown: "Drawdown ≤ 10%",
  trading_cost: "Turnover & cost bounded",
  risk_adjusted_vs_buy_and_hold: "Risk-adjusted edge vs buy & hold",
  risk_adjusted_vs_buy_and_hold_v2: "Alpha & convexity vs buy & hold (since genesis)",
  positive_weeks: "Positive weeks",
  worst_week: "Worst week",
  downside_capture: "Downside capture",
  worst_regime_excess: "Worst regime vs B&H",
};

export function checkLabel(name: string): string {
  return CHECK_LABELS[name] ?? name.replace(/_/g, " ");
}

/** A check's mark: pass / fail, or "n/a" when it could not be judged yet. */
export function checkMark(passed: boolean | null): { text: string; className: string } {
  if (passed === null) return { text: "n/a", className: "obs-bench-check-na" };
  return passed
    ? { text: "✓", className: "obs-bench-check-pass" }
    : { text: "✗", className: "obs-bench-check-fail" };
}

export interface RegimeRun {
  readonly label: string;
  readonly start: string;
  readonly end: string;
  readonly days: number;
}

/** Consecutive days sharing a label, collapsed into runs (the regime strip). */
export function regimeRuns(
  days: readonly BenchmarkRegimeDay[],
  key: "organism" | "rule",
): RegimeRun[] {
  const runs: RegimeRun[] = [];
  for (const day of days) {
    const label = day[key];
    const last = runs[runs.length - 1];
    if (last && last.label === label) {
      runs[runs.length - 1] = { ...last, end: day.date, days: last.days + 1 };
    } else {
      runs.push({ label, start: day.date, end: day.date, days: 1 });
    }
  }
  return runs;
}

const RULE_REGIME_CLASSES: Readonly<Record<string, string>> = {
  trend_up: "obs-regime-up",
  trend_down: "obs-regime-down",
  chop: "obs-regime-chop",
  high_vol: "obs-regime-vol",
  warmup: "obs-regime-unknown",
};

/** A stable CSS class per regime label: the rule's named regimes get their own;
 * the organism's unnamed detector states S0..S3 get one of four neutral tints. */
export function regimeClass(label: string): string {
  const named = RULE_REGIME_CLASSES[label];
  if (named) return named;
  const match = /^S(\d+)$/.exec(label);
  if (match) return `obs-regime-s${Number(match[1]) % 4}`;
  return "obs-regime-unknown";
}

export function regimeText(label: string): string {
  switch (label) {
    case "trend_up":
      return "trend up";
    case "trend_down":
      return "trend down";
    case "high_vol":
      return "high vol";
    case "uniform":
      return "no regime yet";
    default:
      return label;
  }
}

export interface FleetBenchmarkSummary {
  /** Live minus buy-and-hold since genesis (`null` when not published). */
  readonly excessVsBuyAndHold: number | null;
  readonly liveReturn: number | null;
  readonly maxDrawdown: number | null;
  readonly gate: VerdictText;
}

/** The fleet card's one-liner: return vs B&H, max drawdown, rolling gate. */
export function fleetBenchmarkSummary(
  doc: BenchmarksDocument | null,
): FleetBenchmarkSummary | null {
  if (!doc || doc.status !== "ok" || !doc.stats) return null;
  return {
    excessVsBuyAndHold: doc.relative?.buy_and_hold.excess_return ?? null,
    liveReturn: doc.stats.live.total_return,
    maxDrawdown: doc.stats.live.max_drawdown,
    gate: verdictText(doc.gate?.status, doc.days_observed, doc.min_days),
  };
}

/** Re-render key: everything the panel draws changes with the as-of sample. */
export function benchmarksFingerprint(doc: BenchmarksDocument | null): string {
  if (!doc) return "none";
  return `${doc.status}|${doc.as_of_seq}|${doc.as_of_event_time ?? ""}`;
}

/** `role.benchmarks()`, isolating a fetch failure to `null` so it never
 * rejects a `Promise.all` that also awaits more critical data (the fleet
 * card's NAV/day P&L) — same reason as `fetchSizingSafely`. */
export async function fetchBenchmarksSafely(
  role: Pick<RoleSnapshot, "benchmarks">,
): Promise<BenchmarksDocument | null> {
  try {
    return await role.benchmarks();
  } catch (error) {
    console.warn("observatory: benchmarks fetch failed; continuing without them", error);
    return null;
  }
}
